// Gardening dials: soft additive biases on the composited target (never hard walls),
// and 2D mood-pad Shepard interpolation. Pure.

import type { AxisName, AxisVector, Mood } from '@shared/parameterState';
import { AXES, MOODS } from '@shared/parameterState';
import { MOOD_TARGETS, PAD_LANDMARKS } from './moods';

export interface GardeningDials {
  /** -1 calm .. +1 active (biases density). */
  calmActive: number;
  /** -1 dark .. +1 bright (biases brightness + register). */
  darkBright: number;
  /** -1 pure .. +1 rough (biases harmonicity + wildProb). */
  pureRough: number;
  /** 0 park .. 1 explore (scales LFO depth + transition spread). */
  roaming: number;
  /** 2D pad in [0,1]^2; null = not engaged. */
  pad: { x: number; y: number } | null;
}

export function defaultDials(): GardeningDials {
  return { calmActive: 0, darkBright: 0, pureRough: 0, roaming: 0, pad: null };
}

/** Additive per-axis dial bias (caller clamps after compositing). */
export function dialBias(axis: AxisName, dials: GardeningDials): number {
  switch (axis) {
    case 'density':
      return 0.2 * dials.calmActive;
    case 'brightness':
      return 0.25 * dials.darkBright;
    case 'register':
      return 0.1 * dials.darkBright;
    case 'harmonicity':
      return 0.25 * dials.pureRough;
    default:
      return 0;
  }
}

const SHEPARD_EPS = 0.01;

/** Inverse-distance (power 2) Shepard weights over the three mood landmarks. */
export function padWeights(pad: { x: number; y: number }): Record<Mood, number> {
  const raw = {} as Record<Mood, number>;
  let sum = 0;
  for (const m of MOODS) {
    const lm = PAD_LANDMARKS[m];
    const dx = pad.x - lm.x;
    const dy = pad.y - lm.y;
    const dist2 = dx * dx + dy * dy;
    const w = 1 / (SHEPARD_EPS + dist2);
    raw[m] = w;
    sum += w;
  }
  for (const m of MOODS) raw[m] /= sum;
  return raw;
}

/** Pad-blended mood target vector (full override of Markov axis targets). */
export function padBlendTarget(pad: { x: number; y: number }): AxisVector {
  const w = padWeights(pad);
  const out = {} as AxisVector;
  for (const ax of AXES) {
    let v = 0;
    for (const m of MOODS) v += w[m] * MOOD_TARGETS[m][ax];
    out[ax] = v;
  }
  return out;
}

/** Highest-weight mood under the pad (used for articulation picks). */
export function padDominantMood(pad: { x: number; y: number }): Mood {
  const w = padWeights(pad);
  let best: Mood = MOODS[0];
  for (const m of MOODS) if (w[m] > w[best]) best = m;
  return best;
}
